import React, { useState } from 'react';
import { Product, ICONS_MAP, PRODUCTS } from '@/app/data';
import { ShoppingCart, ArrowLeft, Star, Share2, CheckCircle2, Tag, Clock, ShieldAlert, Ruler, Info, AlertCircle, FileText, Download, ExternalLink, User, HelpCircle, ArrowRight, Circle, Check, Eye } from 'lucide-react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { ProductCard } from '@/app/components/ProductCard';
import { useNavigate } from 'react-router-dom';
import { CertificateModal } from '@/app/components/CertificateModal';
import { RedirectModal } from '@/app/components/RedirectModal';

interface PDPProps {
  product: Product;
  onAddToCart: (product: Product) => void;
  onBack: () => void;
}

export const PDP: React.FC<PDPProps> = ({ product, onAddToCart, onBack }) => {
  const { features, recommendedUses, keywords, images, storeLinks } = product;
  const [activeTab, setActiveTab] = React.useState<'specs' | 'guide'>('specs');
  const [mainImage, setMainImage] = React.useState(product.image);
  const [isCertModalOpen, setIsCertModalOpen] = useState(false);
  
  // Redirect Modal State
  const [isRedirectModalOpen, setIsRedirectModalOpen] = useState(false);
  const [redirectStore, setRedirectStore] = useState<{name: string, url: string} | null>(null);

  const navigate = useNavigate();
  
  // Reset main image when product changes (e.g. from recommended clicks)
  React.useEffect(() => {
    setMainImage(product.image);
    window.scrollTo(0, 0);
  }, [product]);
  
  // Filter active features to display icons
  const activeFeatures = Object.entries(features).filter(([_, isActive]) => isActive);

  // Use images array from product data
  const thumbnails = images;

  // Store logos lookup
  const STORE_LOGOS: Record<string, string> = {
    'Elektra': '/elektra.png',
    'ITALIKA': '/italika.png'
  };

  // Carousel Settings
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 1,
    arrows: false,
    mobileFirst: true, // Use min-width breakpoints
    responsive: [] // Empty responsive array to prevent react-slick crash
  };

  const handleProductClick = (p: Product) => {
    navigate(`/product/${p.id}`);
  };

  const handleExternalLink = (name: string, url: string) => {
    setRedirectStore({ name, url });
    setIsRedirectModalOpen(true);
  };

  return (
    <div className="bg-[#F9FAFB] min-h-screen pb-20">
      <CertificateModal 
        isOpen={isCertModalOpen} 
        onClose={() => setIsCertModalOpen(false)} 
        productName={product.name}
        certificateUrl={product.certificateUrl}
        certificationType={product.certificationType}
      />

      <RedirectModal 
        isOpen={isRedirectModalOpen} 
        onClose={() => setIsRedirectModalOpen(false)} 
        storeName={redirectStore?.name || ''} 
        redirectUrl={redirectStore?.url || ''} 
      />

      {/* Mobile Back Header */}
      <div className="md:hidden sticky top-[68px] z-30 bg-white/80 backdrop-blur-md p-4 flex justify-between items-center border-b border-gray-100">
        <button onClick={onBack} className="flex items-center gap-2 text-[#061970] font-bold">
          <ArrowLeft className="w-5 h-5" /> Volver
        </button>
        <button className="text-gray-400">
            <Share2 className="w-5 h-5" />
        </button>
      </div>

      <div className="container mx-auto px-4 py-6 md:py-12">
        <button onClick={onBack} className="hidden md:flex items-center gap-2 text-gray-500 hover:text-[#FF7F32] mb-8 font-bold uppercase text-sm tracking-widest transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver al catálogo
        </button>

        <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-start mb-20">
          {/* Left Column: Images */}
          <div className="flex flex-col gap-4 md:gap-6">
            {/* Main Image Container - Responsive Square */}
            <div className="w-full max-w-[500px] aspect-square mx-auto rounded-[4px] overflow-hidden flex items-center justify-center relative group bg-white">
                <img 
                  src={mainImage} 
                  alt={product.name}
                  className="w-full h-full object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105 p-4 md:p-0"
                />
            </div>

            {/* Thumbnails Row */}
            <div className="flex justify-center gap-2 md:gap-4 px-2">
                {thumbnails.map((thumb, idx) => (
                    <button
                        key={idx}
                        onClick={() => setMainImage(thumb)}
                        className={`
                            relative w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 bg-white
                            ${mainImage === thumb ? 'border-[#459EDF] ring-2 ring-[#459EDF] ring-offset-2' : 'border-gray-200 hover:border-gray-300 opacity-70 hover:opacity-100'}
                        `}
                    >
                        <img src={thumb} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                ))}
            </div>
          </div>

          {/* Right Column: Details */}
          <div className="flex flex-col h-full px-2 md:px-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
                {product.category.map((cat, idx) => (
                  <span 
                    key={idx}
                    className="bg-[#459EDF]/10 text-[#459EDF] px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest"
                  >
                    {cat}
                  </span>
                ))}
                
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-black italic text-[#061970] uppercase mb-4 leading-none tracking-tighter">
              {product.name}
            </h1>

            <p className="text-gray-600 leading-relaxed mb-6 text-base md:text-lg">
              {product.description}
            </p>

            {/* Product Certification Section */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-5 mb-6 md:mb-8 shadow-sm flex flex-col sm:flex-row gap-4 md:gap-5 items-center sm:items-start">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 text-gray-400">
                    <ShieldAlert className="w-6 h-6 md:w-8 md:h-8 opacity-50" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                    <h3 className="font-black italic text-[#061970] uppercase tracking-wide text-xs md:text-sm mb-1">Certificación Oficial</h3>
                    <p className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4 leading-snug">
                      Este producto cumple con los estándares internacionales de seguridad{' '}
                      {product.certificationType === 'ECE' && 'ECE 22.06'}
                      {product.certificationType === 'DOT' && 'DOT'}
                      {product.certificationType === 'ECE_DOT' && 'ECE 22.06 y DOT'}
                      {!product.certificationType && 'certificados'}.
                    </p>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-2 md:gap-3">
                        <button 
                            onClick={() => setIsCertModalOpen(true)}
                            className="flex items-center gap-2 px-3 md:px-4 py-2 bg-[#061970] text-white text-xs font-bold uppercase rounded-full hover:bg-[#0a2399] transition-colors shadow-md active:scale-95"
                        >
                            <Eye className="w-3 md:w-3.5 h-3 md:h-3.5" />
                            Ver certificado
                        </button>
                    </div>
                </div>
            </div>

            {/* Recommended Uses Section (New) */}
            {recommendedUses && recommendedUses.length > 0 && (
                <div className="bg-[#459EDF]/5 border border-[#459EDF]/20 rounded-xl p-4 md:p-5 mb-6 md:mb-8">
                    <div className="flex items-center gap-3 mb-3">
                        <CheckCircle2 className="w-4 md:w-5 h-4 md:h-5 text-[#FF7F32] stroke-[2.5]" />
                        <h3 className="font-black italic text-[#061970] uppercase tracking-wide text-xs md:text-sm">USOS RECOMENDADOS</h3>
                    </div>
                    <ul className="space-y-2">
                        {recommendedUses.map((use, i) => (
                            <li key={i} className="flex items-center gap-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#459EDF] flex-shrink-0" />
                                <span className="text-gray-700 font-medium text-xs md:text-sm">{use}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Tabs Section */}
            <div className="mb-6 md:mb-8">
                <div className="flex border-b border-gray-200 mb-4 md:mb-6 -mx-2 px-2 md:mx-0 md:px-0">
                    <button
                        onClick={() => setActiveTab('specs')}
                        className={`pb-3 px-3 md:px-4 font-bold uppercase tracking-wider text-xs md:text-sm transition-colors border-b-2 flex-1 md:flex-none text-center ${activeTab === 'specs' ? 'border-[#FF7F32] text-[#061970]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                        Especificaciones
                    </button>
                    <button
                        onClick={() => setActiveTab('guide')}
                        className={`pb-3 px-3 md:px-4 font-bold uppercase tracking-wider text-xs md:text-sm transition-colors border-b-2 flex-1 md:flex-none text-center ${activeTab === 'guide' ? 'border-[#FF7F32] text-[#061970]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                    >
                        Guía de Tallas
                    </button>
                </div>

                <div className="min-h-[300px]">
                    {activeTab === 'specs' && (
                        <div className="space-y-4 md:space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-3">
                                {activeFeatures.map(([key, _]) => {
                                const item = ICONS_MAP[key as keyof typeof ICONS_MAP];
                                if (!item) return null;
                                const Icon = item.icon;
                                return (
                                    <div key={key} className="flex flex-col items-center justify-center gap-2 p-2 md:p-3 rounded-xl bg-white border border-gray-100 text-center h-20 md:h-24">
                                        <div className="p-1 md:p-1.5 bg-gray-50 rounded-full shadow-sm text-[#061970]">
                                            <Icon className="w-3.5 h-3.5 md:w-4 md:h-4" />
                                        </div>
                                        <span className="text-[9px] md:text-[10px] font-bold text-gray-700 uppercase tracking-wide leading-tight">{item.label}</span>
                                    </div>
                                );
                                })}
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 shadow-sm">
                                <h3 className="font-bold text-[#061970] uppercase tracking-wider text-xs mb-2 md:mb-3 border-b border-gray-100 pb-2">
                                    Mantenimiento
                                </h3>
                                <div className="space-y-2 md:space-y-3">
                                    <div className="flex items-start gap-2 md:gap-3">
                                        <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#459EDF] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-bold text-gray-800">Vida Útil: 3 Años</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-2 md:gap-3">
                                        <ShieldAlert className="w-3.5 h-3.5 md:w-4 md:h-4 text-[#FF7F32] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-bold text-gray-800">Chequeo Mensual</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'guide' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-300 pb-4">
                            
                            {/* Step 1: Mide Tu Cabeza */}
                            <div className="flex gap-4 items-start">
                                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">1</div>
                                <div className="space-y-3 pt-1 flex-1">
                                    <div>
                                        <h3 className="text-sm font-black text-[#061970] uppercase">MIDE TU CABEZA</h3>
                                        <p className="text-xs text-gray-600 leading-relaxed mt-1">
                                            Mide la circunferencia de tu cabeza. Pasa la cinta métrica por encima de las cejas y alrededor de la parte más ancha de tu cabeza.
                                        </p>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-4 items-start">
                                        <div className="bg-white rounded-lg p-3 flex gap-3 items-start border border-gray-200 shadow-sm flex-1">
                                            <Info className="w-4 h-4 text-[#459EDF] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-[10px] font-bold text-gray-500 uppercase mb-0.5">CONSEJO PRO</p>
                                                <p className="text-[11px] text-gray-600 leading-tight">
                                                    Si no tienes cinta métrica, usa un cordón y luego mídelo con una regla.
                                                </p>
                                            </div>
                                        </div>
                                        <div className="w-[128px] h-[159px] bg-gray-200 rounded-lg flex-shrink-0 mx-auto md:mx-0">
                                             <img src='/guia_de_tallas/Cabeza_medidas.png'></img>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Step 2: Forma de la Cabeza */}
                            <div className="flex gap-4 items-start">
                                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">2</div>
                                <div className="space-y-4 pt-1 flex-1 w-full">
                                    <div>
                                        <h3 className="text-sm font-black text-[#061970] uppercase">FORMA DE LA CABEZA</h3>
                                        <p className="text-xs text-gray-600 leading-relaxed mt-1">
                                            Identifica la forma de tu cabeza (vista superior). Nuestros cascos están diseñados principalmente para formas <span className="font-bold text-gray-800">Intermedias</span>.
                                        </p>
                                    </div>
                                    <div className="flex justify-between items-end px-2 sm:px-8">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full border border-gray-300"></div>
                                            <span className="text-[10px] text-gray-500 font-bold uppercase">REDONDA</span>
                                        </div>
                                        <div className="flex flex-col items-center gap-2 relative -top-1">
                                            <div className="absolute -top-6 text-[#FF7F32]">
                                                <div className="w-4 h-4 rounded-full border border-[#FF7F32] flex items-center justify-center">
                                                    <Check className="w-3 h-3" />
                                                </div>
                                            </div>
                                            <div className="w-10 h-14 sm:w-12 sm:h-16 rounded-[24px] border-2 border-[#FF7F32] bg-[#FF7F32]/5"></div>
                                            <span className="text-[10px] text-[#FF7F32] font-bold uppercase">INTERMEDIA</span>
                                        </div>
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-14 h-10 sm:w-16 sm:h-12 rounded-[24px] border border-gray-300"></div>
                                            <span className="text-[10px] text-gray-500 font-bold uppercase">LARGA</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Step 3: Tabla de Medidas */}
                            <div className="flex gap-4 items-start">
                                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">3</div>
                                <div className="space-y-3 pt-1 flex-1 w-full overflow-hidden">
                                    <h3 className="text-sm font-black text-[#061970] uppercase">TABLA DE MEDIDAS</h3>
                                    <div className="overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
                                        <table className="w-full text-xs text-left">
                                            <thead className="bg-gray-50 uppercase text-gray-500 font-bold border-b border-gray-100 text-[10px]">
                                                <tr>
                                                    <th className="px-3 py-2 w-1/3">TALLA</th>
                                                    <th className="px-3 py-2 w-1/3">PULGADAS</th>
                                                    <th className="px-3 py-2 w-1/3">CENTÍMETROS</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {[
                                                    { size: 'CHICO (S)', in: '21 1/2" - 22"', cm: '55 - 56 CM' },
                                                    { size: 'MEDIANO (M)', in: '22 1/4" - 22 3/4"', cm: '57 - 58 CM' },
                                                    { size: 'GRANDE (L)', in: '23" - 23 1/2"', cm: '59 - 60 CM' },
                                                    { size: 'X-G (XL)', in: '23 3/4" - 24 1/2"', cm: '61 - 62 CM' },
                                                    { size: 'XX-G (XXL)', in: '24 3/4" - 25 3/16"', cm: '63 - 64 CM' }
                                                ].map((row) => (
                                                    <tr key={row.size} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-3 py-2.5 font-bold text-[#061970] text-[11px]">{row.size}</td>
                                                        <td className="px-3 py-2.5 text-gray-600 font-medium text-[11px]">{row.in}</td>
                                                        <td className="px-3 py-2.5 font-bold text-[#FF7F32] text-[11px]">{row.cm}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Step 4: Comprueba el Ajuste */}
                            <div className="flex gap-4 items-start">
                                <div className="flex-shrink-0 w-8 h-8 bg-[#FF7F32] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">4</div>
                                <div className="space-y-3 pt-1 flex-1">
                                    <div>
                                        <h3 className="text-sm font-black text-[#061970] uppercase">COMPRUEBA EL AJUSTE</h3>
                                        <p className="text-xs text-gray-600 leading-relaxed mt-1">
                                            El casco no debe moverse al girar la cabeza bruscamente.
                                        </p>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-4 items-start">
                                        <div className="bg-[#459EDF]/5 rounded-lg p-3 flex gap-3 items-start border border-[#459EDF]/20 flex-1">
                                            <Info className="w-4 h-4 text-[#459EDF] mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="text-[10px] font-bold text-[#061970] uppercase mb-0.5">Prueba de "Mejillas de Ardilla"</p>
                                                <p className="text-[11px] text-gray-600 leading-tight mb-2">
                                                    Debes sentir las almohadillas presionando ligeramente tus mejillas hacia arriba.
                                                </p>
                                                <div className="flex items-center gap-1.5 text-[9px] font-black text-[#459EDF] uppercase tracking-wide">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                    <span>LAS ALMOHADILLAS CEDEN TRAS 15-20 HORAS</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="w-[224px] h-[211px] bg-gray-200 rounded-lg flex-shrink-0 mx-auto md:mx-0">
                                             <img src='/guia_de_tallas/Casco_medidas.png'></img>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Alert */}
                            <div className="bg-[#FF7F32] rounded-lg p-3 flex items-center justify-center gap-2 text-white shadow-md mt-4">
                                <AlertCircle className="w-4 h-4" />
                                <span className="text-xs font-black uppercase tracking-wide">¡MIDE BIEN TU CABEZA ANTES DE ORDENAR!</span>
                            </div>

                        </div>
                    )}
                </div>
            </div>

            {/* External Actions (Updated Style from Reference) */}
            <div className="mt-auto space-y-4 md:space-y-6 pt-6 md:pt-8 border-t border-gray-100">
                <div>
                    <h3 className="font-black italic text-[#061970] uppercase mb-3 md:mb-4 text-xs md:text-sm tracking-wide">DISPONIBLE EN:</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                        {storeLinks?.map((store) => (
                            <button 
                                key={store.name}
                                onClick={() => handleExternalLink(store.name, store.url)}
                                className="flex items-center justify-center h-12 md:h-14 bg-white border-2 border-[#FF7F32] rounded-full hover:shadow-lg hover:border-orange-500 transition-all active:scale-[0.98] group overflow-hidden relative cursor-pointer"
                            >
                                <img 
                                    src={STORE_LOGOS[store.name]} 
                                    alt={store.name} 
                                    className="h-10 md:h-12 object-contain filter group-hover:brightness-90 transition-all"
                                />
                            </button>
                        ))}
                    </div>
                    <p className="text-[10px] md:text-xs text-gray-400 italic mt-2 md:mt-3 text-center sm:text-left">
                        * Redirige a nuestro socio de confianza para una compra segura.
                    </p>
                </div>
                
                {/* Certificate Button Removed Here */}
            </div>
          </div>
        </div>

            {/* Recommended Products Carousel */}
            <div className="border-t border-gray-100 pt-16 mt-8">
                <div className="flex items-end justify-between mb-8 px-2">
                    <div>
                        <h2 className="text-3xl font-black italic text-[#061970] uppercase tracking-tighter mb-2">También te puede interesar</h2>
                        <p className="text-gray-500 text-sm">Completa tu equipo con nuestra selección</p>
                    </div>
                    <button 
                        onClick={onBack}
                        className="hidden sm:flex items-center gap-2 text-[#FF7F32] font-bold uppercase text-xs tracking-widest hover:underline"
                    >
                        Ver Catálogo <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
                
                {/* Mobile/Tablet Slider (< 1024px) */}
                <div className="w-full block lg:hidden pb-12">
                    <Slider {...carouselSettings}>
                        {PRODUCTS.slice(0, 6).map((p) => (
                            <div key={p.id} className="px-2 h-full">
                                <ProductCard 
                                    product={p} 
                                    onAddToCart={onAddToCart}
                                    onClick={handleProductClick}
                                />
                            </div>
                        ))}
                    </Slider>
                </div>

                {/* Desktop Grid (>= 1024px) */}
                <div className="hidden lg:grid grid-cols-4 gap-6 pb-12">
                    {PRODUCTS.slice(0, 4).map((p) => (
                        <ProductCard 
                            key={p.id}
                            product={p} 
                            onAddToCart={onAddToCart}
                            onClick={handleProductClick}
                        />
                    ))}
                </div>
                
                {/* Mobile CTA Button - Catalog Redirect */}
                <div className="flex justify-center mt-6 sm:hidden px-4">
                    <button 
                        onClick={onBack}
                        className="w-full flex items-center justify-center gap-2 bg-[#061970] text-white font-black uppercase text-xs py-4 rounded-full shadow-lg hover:bg-[#0a2399] transition-all active:scale-95 tracking-widest"
                    >
                        Ver Catálogo Completo <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
      </div>
    </div>
  );
};
